import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_MAX = 10;
const POLL_INTERVAL = 2000;

export default function TransferStatus({ kind = "success" }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: null, transaction: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (kind !== "success") {
      setState({ status: "cancelled" });
      return;
    }
    if (!sessionId) {
      setState({ status: "missing" });
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/transfer/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment_status: data.payment_status, transaction: data.transaction });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= POLL_MAX) {
          setState({ status: "timeout", payment_status: data.payment_status, transaction: data.transaction });
          return;
        }
        setTimeout(tick, POLL_INTERVAL);
      } catch {
        attempts.current += 1;
        if (attempts.current >= POLL_MAX) setState({ status: "error" });
        else setTimeout(tick, POLL_INTERVAL);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, kind]);

  return (
    <div data-testid="page-transfer-status" className="max-w-2xl mx-auto px-4 sm:px-8 py-16 text-center">
      {state.status === "polling" && (
        <>
          <Loader2 size={48} className="mx-auto animate-spin text-[color:var(--papicoo-gold)]" />
          <h1 className="font-display text-3xl mt-6">{T("On vérifie ton paiement…")}</h1>
          <p className="text-sm text-[color:var(--papicoo-muted)] mt-2">{T("Quelques secondes — ne ferme pas cette page.")}</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
          <h1 className="font-display text-3xl mt-6">{T("Paiement reçu 🌹")}</h1>
          <p className="text-[color:var(--papicoo-muted)] mt-3">
            <strong>{T("Salaam.")}</strong> Le transfert vers {state.transaction?.metadata?.recipient_name || T("le destinataire")} a été initié. Papicoo te confirme par email sous 24 h.
          </p>
          {state.transaction?.metadata && (
            <div className="mt-6 papicoo-card p-5 text-left text-sm space-y-1">
              <div>{T("Montant envoyé :")} <strong>{state.transaction.metadata.send_eur} €</strong></div>
              <div>{T("Frais Papicoo :")} <strong>{state.transaction.metadata.fee_eur} €</strong></div>
              <div>{T("Destinataire :")} <strong>{state.transaction.metadata.recipient_name}</strong></div>
              <div>{T("Mobile money :")} <strong>{state.transaction.metadata.payout_method}</strong> · {state.transaction.metadata.recipient_phone}</div>
            </div>
          )}
        </>
      )}
      {state.status === "cancelled" && (
        <>
          <XCircle size={56} className="mx-auto text-amber-600" />
          <h1 className="font-display text-3xl mt-6">{T("Paiement annulé")}</h1>
          <p className="text-[color:var(--papicoo-muted)] mt-3">{T("Tu peux relancer quand tu veux — aucune somme prélevée.")}</p>
        </>
      )}
      {state.status === "expired" && (
        <>
          <XCircle size={56} className="mx-auto text-red-600" />
          <h1 className="font-display text-3xl mt-6">{T("Session expirée")}</h1>
          <p className="text-[color:var(--papicoo-muted)] mt-3">{T("La session Stripe a expiré. Recommence depuis la page Transfert.")}</p>
        </>
      )}
      {state.status === "timeout" && (
        <>
          <Loader2 size={48} className="mx-auto text-amber-600" />
          <h1 className="font-display text-3xl mt-6">{T("Délai de vérification dépassé")}</h1>
          <p className="text-[color:var(--papicoo-muted)] mt-3">{T("Pas d'inquiétude — Papicoo vérifie en arrière-plan et te confirme par email.")}</p>
        </>
      )}
      {state.status === "missing" && (
        <>
          <XCircle size={56} className="mx-auto text-red-600" />
          <h1 className="font-display text-3xl mt-6">{T("Session introuvable")}</h1>
        </>
      )}
      {state.status === "error" && (
        <>
          <XCircle size={56} className="mx-auto text-red-600" />
          <h1 className="font-display text-3xl mt-6">{T("Erreur de vérification")}</h1>
          <p className="text-[color:var(--papicoo-muted)] mt-3">{T("Réessaie dans un instant, ou contacte Papicoo.")}</p>
        </>
      )}
      <Link to="/transfer" data-testid="back-transfer" className="mt-8 inline-flex items-center gap-2 text-sm papicoo-link-underline">
        <ArrowLeft size={14} /> {T("Revenir au transfert")}
      </Link>
    </div>
  );
}
